/**
 * StepRail.tsx
 *
 * Column 2 of the group processing drawer: the protocol's steps in flow order,
 * with conditional branches shown as indented Yes / No groups, plus the group
 * roster entry. Selecting a row drives what column 3 renders.
 */

import React from "react";
import { Tooltip } from "antd";
import { LockOutlined } from "@ant-design/icons";

import type { GroupStepInstance, ProtocolStep } from "../../../../../../types/lab/ProtocolTypes";
import { buildRailRows } from "./flowTree";
import { AUTO_STEP_TYPES, stepStatusColor, stepTypeCfg } from "./stepMeta";

export type RailSelection = { kind: "step"; stepId: number } | { kind: "instances" };

interface StepRailProps {
	steps: ProtocolStep[];
	stepInstances: GroupStepInstance[];
	/** Step numbers currently open across all branches. */
	activeStepNumbers: number[];
	/** Number of samples or analyses in the group. */
	instanceCount: number;
	instanceLabel: "Samples" | "Analyses";
	selection: RailSelection | null;
	onSelect: (selection: RailSelection) => void;
	canOpenStep: (step: ProtocolStep) => boolean;
	/** Steps on branches this group never took — kept out of the rail entirely. */
	hiddenStepIds?: Set<number>;
}

const RAIL_STYLES = `
.mg-rail-row {
	display: flex;
	align-items: center;
	gap: 6px;
	width: 100%;
	padding: 4px 8px;
	border: 0;
	border-left: 2px solid transparent;
	background: transparent;
	color: inherit;
	font-size: 11px;
	text-align: left;
	transition: background 0.15s ease;
}
.mg-rail-row[data-clickable="true"] { cursor: pointer; }
.mg-rail-row[data-clickable="true"]:hover { background: var(--mg-primary-bg); }
.mg-rail-row[data-selected="true"] {
	background: var(--mg-primary-bg-hover);
	border-left-color: var(--mg-primary);
}
.mg-rail-row:focus-visible { outline: 2px solid var(--mg-primary); outline-offset: -2px; }
.mg-rail-heading {
	padding: 6px 8px 4px;
	font-size: var(--mg-fs-xxs);
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--mg-text-secondary);
}
@media (prefers-reduced-motion: reduce) { .mg-rail-row { transition: none; } }
`;

const StatusDot: React.FC<{ status: string | undefined; title: string }> = ({ status, title }) => (
	<span
		title={title}
		style={{
			flexShrink: 0,
			width: 8,
			height: 8,
			borderRadius: "50%",
			background: stepStatusColor(status),
		}}
	/>
);

const StepRail: React.FC<StepRailProps> = ({
	steps,
	stepInstances,
	activeStepNumbers,
	instanceCount,
	instanceLabel,
	selection,
	onSelect,
	canOpenStep,
	hiddenStepIds,
}) => {
	const rows = React.useMemo(
		() => buildRailRows(steps, hiddenStepIds),
		[steps, hiddenStepIds],
	);
	const gsiByStepId = React.useMemo(
		() => new Map(stepInstances.map((gsi) => [gsi.step_id, gsi])),
		[stepInstances],
	);

	return (
		<div
			style={{
				width: 200,
				flexShrink: 0,
				height: "100%",
				overflowY: "auto",
				borderRight: "1px solid var(--mg-border)",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<style>{RAIL_STYLES}</style>

			<div className="mg-rail-heading">Workflow</div>

			{rows.length === 0 && (
				<div style={{ padding: "4px 10px", fontSize: 11, color: "var(--mg-text-disabled)" }}>
					No steps defined
				</div>
			)}

			{rows.map((row) => {
				if (row.kind === "branch") {
					const tone = row.tone === "yes" ? "var(--mg-success)" : "var(--mg-warning)";
					return (
						<div
							key={row.key}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								padding: "6px 8px 2px",
								paddingLeft: 8 + row.depth * 10,
							}}
						>
							<span
								style={{
									fontSize: 9,
									fontWeight: 600,
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									color: tone,
								}}
							>
								{row.label}
							</span>
							<span style={{ flex: 1, height: 1, background: "var(--mg-border)" }} />
						</div>
					);
				}

				const { step, depth } = row;
				const gsi = gsiByStepId.get(step.id);
				const status = gsi?.status ?? "pending";
				const clickable = canOpenStep(step);
				const isActive = activeStepNumbers.includes(step.step_number);
				const isSelected = selection?.kind === "step" && selection.stepId === step.id;
				const isAutoStep = AUTO_STEP_TYPES.has(step.step_type);
				const { label: typeLabel } = stepTypeCfg(step.step_type);

				const tooltip = clickable
					? `${step.name} · ${typeLabel}`
					: `${step.name} · ${typeLabel} — ${
							status === "completed"
								? "completed"
								: isAutoStep
								? "runs automatically"
								: "not yet available"
					  }`;

				return (
					<Tooltip key={row.key} title={tooltip} placement="right" mouseEnterDelay={0.6}>
						{/* Locked rows stay in the DOM as buttons — not `disabled` — so the
						    tooltip explaining why still opens on hover. */}
						<button
							type="button"
							className="mg-rail-row"
							data-clickable={clickable}
							data-selected={isSelected}
							aria-disabled={!clickable}
							tabIndex={clickable ? 0 : -1}
							aria-current={isSelected ? "true" : undefined}
							onClick={clickable ? () => onSelect({ kind: "step", stepId: step.id }) : undefined}
							style={{ paddingLeft: 8 + depth * 10 }}
						>
							<span
								style={{
									flexShrink: 0,
									minWidth: 14,
									fontSize: 10,
									fontVariantNumeric: "tabular-nums",
									color: isActive ? "var(--mg-primary)" : "var(--mg-text-secondary)",
									fontWeight: isActive ? 600 : 400,
								}}
							>
								{step.step_number}
							</span>
							<span
								style={{
									flex: 1,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									color: clickable ? "var(--mg-text)" : "var(--mg-text-disabled)",
								}}
							>
								{step.name}
							</span>
							{/* Lock only where a technician could otherwise expect to act — an
							    automated step is not locked, it simply isn't theirs to open. */}
							{!clickable && !isAutoStep && status !== "completed" && (
								<LockOutlined style={{ fontSize: 9, color: "var(--mg-text-disabled)" }} />
							)}
							<StatusDot status={status} title={status.replace(/_/g, " ")} />
						</button>
					</Tooltip>
				);
			})}

			<div style={{ height: 1, background: "var(--mg-border)", margin: "8px 0 0" }} />
			<div className="mg-rail-heading">Group</div>
			<button
				type="button"
				className="mg-rail-row"
				data-clickable="true"
				data-selected={selection?.kind === "instances"}
				aria-current={selection?.kind === "instances" ? "true" : undefined}
				onClick={() => onSelect({ kind: "instances" })}
			>
				<span style={{ flex: 1, color: "var(--mg-text)" }}>{instanceLabel}</span>
				<span
					style={{
						flexShrink: 0,
						fontSize: 10,
						fontVariantNumeric: "tabular-nums",
						color: "var(--mg-text-secondary)",
					}}
				>
					{instanceCount}
				</span>
			</button>
		</div>
	);
};

export default StepRail;
